import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Cloud } from "lucide-react";
import { InlineLoadSkeleton } from "../components/common/InlineLoadSkeleton";
import SignupHero from "../components/SignupHero";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { authAPI } from "../services/api";
import { useAuth } from "../context/AuthContext";
import toast from "react-hot-toast";
import styles from "./SignupPage.module.css";

const PRIMARY_REASONS = [
  { value: "", label: "Select primary reason" },
  { value: "test_management", label: "Test Case Management" },
  { value: "quality_assurance", label: "Quality Assurance" },
  { value: "ci_cd", label: "CI/CD Integration" },
  { value: "team_collaboration", label: "Team Collaboration" },
  { value: "automation", label: "Test Automation" },
  { value: "other", label: "Other" },
];

const SignupPage = () => {
  const navigate = useNavigate();
  const { signup, isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    companyName: "",
    primaryReason: "",
    password: "",
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    document.documentElement.classList.remove("dark");
    return () => {};
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      navigate("/projects", { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.firstName?.trim())
      newErrors.firstName = "First name is required";
    if (!formData.lastName?.trim())
      newErrors.lastName = "Last name is required";
    if (!formData.email?.trim()) {
      newErrors.email = "Work email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email";
    }
    if (!formData.companyName?.trim()) {
      newErrors.companyName = "Company name is required";
    } else if (formData.companyName.trim().length < 2) {
      newErrors.companyName = "Company name must be at least 2 characters";
    }
    if (!formData.primaryReason)
      newErrors.primaryReason = "Primary reason is required";
    if (!formData.password) {
      newErrors.password = "Password is required";
    } else if (formData.password.length < 8) {
      newErrors.password = "Password must be at least 8 characters";
    } else if (!/[A-Z]/.test(formData.password)) {
      newErrors.password =
        "Password must contain at least one uppercase letter";
    } else if (!/[a-z]/.test(formData.password)) {
      newErrors.password =
        "Password must contain at least one lowercase letter";
    } else if (!/\d/.test(formData.password)) {
      newErrors.password = "Password must contain at least one number";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setLoading(true);
    setErrors({});
    try {
      const result = await signup({
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: formData.email.trim().toLowerCase(),
        companyName: formData.companyName.trim(),
        primaryReason: formData.primaryReason,
        password: formData.password,
      });
      if (result?.token) {
        toast.success("Account created successfully! Welcome to TestNimbus.");
        navigate("/projects", { replace: true });
      } else if (result?.requires_verification) {
        toast.success("Account created! Please check your email to verify.");
        navigate("/login", { replace: true });
      }
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        err.message ||
        "Signup failed. Please try again.";
      toast.error(msg);
      if (err.response?.data?.error === "USER_EXISTS") {
        setErrors((prev) => ({
          ...prev,
          email: "An account with this email already exists.",
        }));
      }
    } finally {
      setLoading(false);
    }
  };

  const isFormValid =
    formData.firstName?.trim() &&
    formData.lastName?.trim() &&
    formData.email?.trim() &&
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email) &&
    formData.companyName?.trim().length >= 2 &&
    formData.primaryReason &&
    formData.password?.length >= 8 &&
    /[A-Z]/.test(formData.password) &&
    /[a-z]/.test(formData.password) &&
    /\d/.test(formData.password);

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.logo}>
          <Cloud className={styles.logoIcon} />
          <span>TestNimbus</span>
        </div>
        <nav className={styles.nav}>
          <Link to="/#features" className={styles.navLink}>
            Features
          </Link>
          <Link to="/#pricing" className={styles.navLink}>
            Pricing
          </Link>
          <Link to="/#resources" className={styles.navLink}>
            Resources
          </Link>
          <Link to="/login" className={styles.navLink}>
            Login
          </Link>
          <Link to="/register" className={styles.ctaButton}>
            Get Started Free
          </Link>
        </nav>
      </header>

      <div className={styles.layout}>
        <div className={styles.heroWrap}>
          <SignupHero />
        </div>

        <div className={styles.cardWrap}>
          <div className={styles.card}>
            <h1 className={styles.cardTitle}>
              Get started for <span className={styles.freeText}>FREE</span>
            </h1>
            <form onSubmit={handleSubmit} className={styles.form}>
              <div className={styles.row}>
                <div className={styles.field}>
                  <Label htmlFor="firstName">First Name</Label>
                  <Input
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    placeholder="First name"
                    className={styles.input}
                    disabled={loading}
                    data-testid="signup-firstname"
                  />
                  {errors.firstName && (
                    <span className={styles.error}>{errors.firstName}</span>
                  )}
                </div>
                <div className={styles.field}>
                  <Label htmlFor="lastName">Last Name</Label>
                  <Input
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    placeholder="Last name"
                    className={styles.input}
                    disabled={loading}
                    data-testid="signup-lastname"
                  />
                  {errors.lastName && (
                    <span className={styles.error}>{errors.lastName}</span>
                  )}
                </div>
              </div>

              <div className={styles.field}>
                <Label htmlFor="email">Work Email</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="you@company.com"
                  className={styles.input}
                  disabled={loading}
                  data-testid="signup-email"
                />
                {errors.email && (
                  <span className={styles.error}>{errors.email}</span>
                )}
              </div>

              <div className={styles.field}>
                <Label htmlFor="companyName">Company Name</Label>
                <Input
                  id="companyName"
                  name="companyName"
                  value={formData.companyName}
                  onChange={handleChange}
                  placeholder="Your company name"
                  className={styles.input}
                  disabled={loading}
                  data-testid="signup-company"
                />
                {errors.companyName && (
                  <span className={styles.error}>{errors.companyName}</span>
                )}
              </div>

              <div className={styles.field}>
                <Label htmlFor="primaryReason">Primary Reason</Label>
                <select
                  id="primaryReason"
                  name="primaryReason"
                  value={formData.primaryReason}
                  onChange={handleChange}
                  className={styles.select}
                  disabled={loading}
                  data-testid="signup-primary-reason"
                >
                  {PRIMARY_REASONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                {errors.primaryReason && (
                  <span className={styles.error}>{errors.primaryReason}</span>
                )}
              </div>

              <div className={styles.field}>
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Create a password (min 8 chars)"
                  className={styles.input}
                  disabled={loading}
                  data-testid="signup-password"
                />
                {errors.password && (
                  <span className={styles.error}>{errors.password}</span>
                )}
              </div>

              <Button
                type="submit"
                disabled={!isFormValid || loading}
                className={styles.submitBtn}
                data-testid="signup-submit"
              >
                {loading ? (
                  <>
                    <InlineLoadSkeleton className="h-5 w-5 mr-2" />
                    Signing Up...
                  </>
                ) : (
                  "Sign Up"
                )}
              </Button>

              <p className={styles.footer}>
                By signing up, you agree to our{" "}
                <Link to="/terms" className={styles.footerLink}>
                  Terms of Service
                </Link>{" "}
                &{" "}
                <Link to="/privacy" className={styles.footerLink}>
                  Privacy Policy
                </Link>
              </p>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignupPage;
